import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v3Api } from '../../hooks/useApi';

/**
 * Debounced typeahead picker for any entity type supported by
 * /api/comparison/search (Tenant, Property, Category, Subcategory,
 * Group, Floor, Zone, Vertical).
 *
 * Stores { id, label } so the chart legend can render a human-readable
 * name regardless of whether the user typed the underlying ObjectId.
 *
 * Props:
 *   type:   one of the supported entity types
 *   value:  { id, label } | { id: '', label: '' }
 *   onChange(next): emits the new {id, label}
 */
export default function EntitySearchPicker({ type, value, onChange, placeholder }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState(value?.label || '');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);

    // Sync external value → input text (e.g. when URL prefill or row clone hydrates)
    useEffect(() => {
        setQuery(value?.label || '');
    }, [value?.id, value?.label]);

    // Close popover on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    // Debounced fetch
    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await v3Api.comparison.search(type, query, 20);
                setResults(Array.isArray(res?.data) ? res.data : []);
            } catch (_) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 280);
        return () => clearTimeout(debounceRef.current);
    }, [query, type, open]);

    const pick = (it) => {
        onChange({ id: it.id, label: it.label });
        setQuery(it.label);
        setOpen(false);
    };

    return (
        <div ref={wrapRef} style={wrap}>
            <input
                type="text"
                value={query}
                placeholder={placeholder || t('Search {{type}}…', { type: type.toLowerCase() })}
                onFocus={() => setOpen(true)}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                style={input}
            />
            {open && (
                <div style={dropdown}>
                    {loading && <div style={hint}>{t('Searching…')}</div>}
                    {!loading && results.length === 0 && <div style={hint}>{t('No matches')}</div>}
                    {!loading && results.map((it) => (
                        <div
                            key={`${it.id}-${it.label}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                            style={row}
                        >
                            <div style={{ fontSize: 13, color: 'var(--v3-text-primary)' }}>{it.label}</div>
                            {it.sub && <div style={{ fontSize: 11, color: 'var(--v3-text-muted)' }}>{it.sub}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const wrap = { position: 'relative', flex: 1, minWidth: 180 };
const input = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 13,
    outline: 'none',
};
const dropdown = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    maxHeight: 280,
    overflowY: 'auto',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    zIndex: 50,
};
const row = {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--v3-glass-border)',
};
const hint = { padding: '10px 12px', fontSize: 12, color: 'var(--v3-text-muted)' };
